import { CalendarDays, Clock3, FolderKanban, History, Info, Store, Wrench } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Avatar } from '@/components/ui/avatar'
import { fmtDate, fmtDateTime } from '../../../shared/format'
import type { Project, Task } from '../../types'
import { useStaffLookup } from '../../ui'
import { MemberStack } from '../ProjectBits'
import { PriorityBadge, TaskStatusBadge } from '../StatusBadges'
import { useHistoryText } from '../../history'

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_1fr] items-center gap-2 py-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  )
}

export function TaskInfoCard({ project, task }: { project: Project; task: Task }) {
  const { t } = useTranslation()
  const historyText = useHistoryText()
  const { nameOf } = useStaffLookup()
  const inst = project.installations.find((i) => i.id === task.installationId)
  const person = (id: string | null) =>
    id ? (
      <span className="flex items-center gap-2">
        <Avatar name={nameOf(id)} className="size-6 [&>span]:text-[9px]" />
        <span className="truncate">{nameOf(id)}</span>
      </span>
    ) : (
      <span className="text-muted-foreground">{t('projects.work.unassigned')}</span>
    )
  const withIcon = (Icon: typeof Clock3, text: ReactNode) => (
    <span className="flex items-center gap-2">
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="truncate tabular-nums">{text}</span>
    </span>
  )

  return (
    <aside className="space-y-4">
      <section className="rounded-xl border bg-card shadow-card">
        <h2 className="flex items-center gap-2 border-b px-4 py-3 font-semibold">
          <Info className="size-4 text-muted-foreground" aria-hidden />
          {t('projects.taskDetail.info')}
        </h2>
        <dl className="divide-y px-4">
          <Row label={t('projects.tasks.fields.type')}>{withIcon(Wrench, t(`projects.taskType.${task.type}`))}</Row>
          <Row label={t('projects.tasks.fields.customer')}>
            {inst ? (
              <Link to={`/projects/${project.id}/installations?i=${inst.id}`} className="flex items-center gap-2 text-primary hover:underline">
                <Store className="size-4 shrink-0" aria-hidden />
                <span className="truncate">{inst.customer}</span>
              </Link>
            ) : (
              '—'
            )}
          </Row>
          <Row label={t('projects.taskDetail.project')}>
            <Link to={`/projects/${project.id}`} className="flex items-center gap-2 text-primary hover:underline">
              <FolderKanban className="size-4 shrink-0" aria-hidden />
              <span className="truncate">{project.name}</span>
            </Link>
          </Row>
          <Row label={t('projects.tasks.fields.assignee')}>{person(task.assigneeId)}</Row>
          <Row label={t('projects.tasks.fields.reviewer')}>{person(task.reviewerId)}</Row>
          <Row label={t('projects.tasks.fields.priority')}>
            <PriorityBadge priority={task.priority} compact />
          </Row>
          <Row label={t('projects.tasks.fields.sprint')}>{task.sprint || '—'}</Row>
          <Row label={t('projects.fields.startDate')}>{withIcon(CalendarDays, fmtDate(task.startDate))}</Row>
          <Row label={t('projects.tasks.fields.dueDate')}>{withIcon(CalendarDays, fmtDate(task.dueDate))}</Row>
          <Row label={t('projects.tasks.fields.estimate')}>{withIcon(Clock3, t('projects.taskDetail.hours', { count: task.estimateHours }))}</Row>
          <Row label={t('projects.tasks.fields.spent')}>{withIcon(Clock3, t('projects.taskDetail.hours', { count: task.spentHours }))}</Row>
          <Row label={t('projects.tasks.fields.status')}>
            <TaskStatusBadge status={task.status} />
          </Row>
          <Row label={t('projects.taskDetail.watchers')}>
            <MemberStack names={task.watcherIds.map(nameOf)} max={3} />
          </Row>
        </dl>
      </section>

      <section className="rounded-xl border bg-card shadow-card">
        <h2 className="flex items-center gap-2 border-b px-4 py-3 font-semibold">
          <History className="size-4 text-muted-foreground" aria-hidden />
          {t('projects.taskDetail.history')}
        </h2>
        <ol className="relative space-y-4 p-4">
          {task.history.slice(0, 12).map((h) => (
            <li key={h.id} className="relative flex gap-3">
              <span className="relative z-10 mt-1 size-3 shrink-0 rounded-full border-2 border-primary bg-card" aria-hidden />
              <div className="min-w-0 text-sm">
                <p className="text-xs tabular-nums text-muted-foreground">{fmtDateTime(h.at)}</p>
                <p>{historyText(h)}</p>
                <p className="text-xs text-muted-foreground">{h.by}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>
    </aside>
  )
}
